// src/pages/FlagBadgePreview.js
//
// Página de preview del FlagBadge — pensada solo para QA visual durante el
// diseño. NO entra al sitemap ni a pre-rendering. Acceder en dev por
// /flag-preview con `npm start`.

import React from 'react';
import FlagBadge from '../components/common/FlagBadge';
import { FiMapPin, FiBriefcase } from 'react-icons/fi';

const COUNTRIES = [
  { value: 'Argentina',          label: 'Argentina' },
  { value: 'Brasil',             label: 'Brasil' },
  { value: 'Chile',              label: 'Chile' },
  { value: 'Colombia',           label: 'Colombia' },
  { value: 'México',             label: 'México' },
  { value: 'Estados Unidos',     label: 'Estados Unidos' },
  { value: 'Perú',               label: 'Perú' },
  { value: 'Uruguay',            label: 'Uruguay' },
  { value: 'Paraguay',           label: 'Paraguay' },
  { value: 'Bolivia',            label: 'Bolivia' },
  { value: 'Ecuador',            label: 'Ecuador' },
  { value: 'Venezuela',          label: 'Venezuela' },
  { value: 'Cuba',               label: 'Cuba' },
  { value: 'República Dominicana', label: 'R. Dominicana' },
  { value: 'Puerto Rico',        label: 'Puerto Rico' },
  { value: 'Guatemala',          label: 'Guatemala' },
  { value: 'Honduras',           label: 'Honduras' },
  { value: 'El Salvador',        label: 'El Salvador' },
  { value: 'Costa Rica',         label: 'Costa Rica' },
  { value: 'Panamá',             label: 'Panamá' },
  { value: 'Nicaragua',          label: 'Nicaragua' },
  { value: 'Canadá',             label: 'Canadá' }
];

const SIZES = ['xs', 'sm', 'md', 'lg'];

export default function FlagBadgePreview() {
  return (
    <div className="min-h-screen bg-dark-900 text-white py-10 px-6">
      <div className="max-w-6xl mx-auto space-y-12">

        <header>
          <h1 className="text-3xl font-bold mb-2">FlagBadge — preview</h1>
          <p className="text-gray-400">
            Sticker epoxy redondo de país. Acepta tanto nombre en español
            (&quot;México&quot;) como código ISO (&quot;MX&quot;) y se oculta solo si
            no reconoce el valor.
          </p>
        </header>

        {/* ────────── Tamaños sobre fondo oscuro ────────── */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Tamaños sobre fondo oscuro</h2>
          <div className="bg-dark-800 border border-dark-700 rounded-xl p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {SIZES.map((size) => (
                <div key={size} className="space-y-3">
                  <div className="text-sm text-gray-400 uppercase tracking-wider">
                    size = &quot;{size}&quot; ({{ xs: '14px', sm: '18px', md: '22px', lg: '28px' }[size]})
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    {COUNTRIES.slice(0, 8).map((c) => (
                      <FlagBadge key={c.value} country={c.value} size={size} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ────────── Tamaños sobre fondo claro ────────── */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Sobre fondo claro</h2>
          <div className="bg-white text-dark-900 border border-gray-300 rounded-xl p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {SIZES.map((size) => (
                <div key={size} className="space-y-3">
                  <div className="text-sm text-gray-600 uppercase tracking-wider">
                    size = &quot;{size}&quot;
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    {COUNTRIES.slice(0, 8).map((c) => (
                      <FlagBadge key={c.value} country={c.value} size={size} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ────────── Todos los países (Américas) ────────── */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Todos los países (size md)</h2>
          <div className="bg-dark-800 border border-dark-700 rounded-xl p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {COUNTRIES.map((c) => (
                <div key={c.value} className="flex items-center gap-2">
                  <FlagBadge country={c.value} size="md" />
                  <span className="text-sm text-gray-300">{c.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ────────── Simulación: card de empresa ────────── */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Simulación: card de empresa</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {COUNTRIES.slice(0, 4).map((c, i) => (
              <div
                key={c.value}
                className="bg-dark-800 rounded-xl border border-dark-700 p-5"
              >
                <div className="flex items-start">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-900 to-primary-700 flex items-center justify-center flex-shrink-0">
                    <FiBriefcase className="text-white" size={20} />
                  </div>
                  <div className="ml-4 flex-1 min-w-0">
                    {/* Bandera al lado del nombre */}
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-lg text-white truncate">
                        {['Sound Vision Pro', 'AVTech Studios', 'Iluminar SA', 'Stage Works'][i]}
                      </h3>
                      <FlagBadge country={c.value} size="sm" />
                    </div>
                    <p className="text-gray-300 text-sm flex items-center mt-1">
                      <FiMapPin size={12} className="mr-1" />
                      Capital, {c.label}
                    </p>
                    <p className="text-primary-400 text-xs mt-2">
                      • Producción Integral
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ────────── Simulación: card profesional ────────── */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Simulación: card profesional</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {COUNTRIES.slice(4, 7).map((c, i) => (
              <div
                key={c.value}
                className="bg-dark-800 rounded-xl border border-dark-700 p-5"
              >
                <div className="flex items-start">
                  <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-primary-900 to-primary-700 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">
                      {['JM', 'CL', 'AR'][i]}
                    </span>
                    {/* Variante alternativa: bandera pegada en esquina del avatar */}
                    <span className="absolute -bottom-0.5 -right-0.5">
                      <FlagBadge country={c.value} size="sm" />
                    </span>
                  </div>
                  <div className="ml-4 flex-1 min-w-0">
                    <h3 className="font-bold text-lg text-white truncate">
                      {['Juan Méndez', 'Carla López', 'Andrés Ruiz'][i]}
                    </h3>
                    <p className="text-gray-300 text-sm flex items-center mt-1">
                      <FiMapPin size={12} className="mr-1" />
                      {c.label}
                    </p>
                    <p className="text-primary-400 text-xs mt-2">
                      • Sonidista en vivo
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ────────── Simulación: post del feed ────────── */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Simulación: post del feed</h2>
          <div className="max-w-xl bg-dark-800 rounded-xl border border-dark-700 p-5">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-900 to-primary-700 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">SV</span>
              </div>
              <div className="ml-3 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-white">Sound Vision Pro</span>
                  <FlagBadge country="Argentina" size="xs" />
                  <span className="text-gray-500 text-xs">· hace 2h</span>
                </div>
                <span className="text-gray-400 text-xs">Productora AV</span>
              </div>
            </div>
            <p className="text-gray-200 text-sm mt-3">
              Cerrando producción de la conferencia anual. Equipo top, día largo. 🎚️
            </p>
          </div>
        </section>

        {/* ────────── Notas de integración ────────── */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Notas de integración</h2>
          <div className="bg-dark-800 border border-dark-700 rounded-xl p-6 text-sm text-gray-300 space-y-2">
            <p>
              <span className="text-primary-400 font-mono">size=&quot;xs&quot;</span> — inline con texto (post header, comentarios).
            </p>
            <p>
              <span className="text-primary-400 font-mono">size=&quot;sm&quot;</span> — al lado del nombre en cards (recomendado por defecto).
            </p>
            <p>
              <span className="text-primary-400 font-mono">size=&quot;md&quot;</span> — listados con énfasis geográfico.
            </p>
            <p>
              <span className="text-primary-400 font-mono">size=&quot;lg&quot;</span> — header de perfil público.
            </p>
            <p className="pt-2 border-t border-dark-700 mt-3">
              Si <code className="font-mono text-primary-400">profile.country</code> está vacío o no se reconoce, el badge no renderiza nada (no rompe el layout).
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
